import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, tap } from "rxjs";
import * as RevisionActions from "./actions";
import { AppNotificationService } from "src/app/shared/services/notification.service";
import { translate } from '@jsverse/transloco';
import { IGetRevisionResponse, IRevision, IUpdateRevisionResponse } from "src/app/revisions/models/revision";
import { RevisionService } from "src/app/revisions/services/revision.service";
import { Router } from "@angular/router";

@Injectable()
export class RevisionsEffets {

    ////////////////////
    // GET REVISIONS //
    //////////////////

    // Get Revisions
    getRevisions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.getRevisions), 
            mergeMap(() => {
            return this.revisionService.getRevisions({pageNumber: 0, pageSize: 25}).pipe(
                map((response) => 
                    RevisionActions.getRevisionsSuccess({ revisions: response.data })
                ), 
                catchError((error) =>
                    of(RevisionActions.getRevisionsFailure({ error: error.message }))
                )
            );
        })
        )
    );

    // Get Revision by id
    getRevisionById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.getRevisionById), 
            mergeMap((action) => {
            return this.revisionService.getRevisionById(action.id).pipe(
                map((response) => 
                    RevisionActions.getRevisionByIdSuccess({ revision: response })
                ), 
                catchError((error) =>
                    of(RevisionActions.getRevisionByIdFailure({ error: error.message }))
                )
            );
        })
        )
    );

    // Get Revisions by company
    getRevisionsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.getRevisionsByCompany), 
            mergeMap((action) => {
            return this.revisionService.getRevisionsByCompany({pageNumber: 0, pageSize: 25, companyId: action.companyId}).pipe(
                map((response) => 
                    RevisionActions.getRevisionsByCompanySuccess({ revisions: response.data })
                ), 
                catchError((error) =>
                    of(RevisionActions.getRevisionsByCompanyFailure({ error: error.message }))
                )
            );
        })
        )
    );




    //////////////////////
    // CREATE REVISION //
    ////////////////////

    // Create Revision
    createRevision$ = createEffect(() =>
      this.actions$.pipe(
          ofType(RevisionActions.createRevision),
          mergeMap((action) => 
              this.revisionService.createRevision(action.revision).pipe(
                  map((revision: IGetRevisionResponse) => RevisionActions.createRevisionSuccess({ revision })),
                  catchError((error) => of(RevisionActions.createRevisionFailure({ error: error.message })))
              )
          )
      )
    );

    // Revision created successfully
    createRevisionSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.createRevisionSuccess),
            tap((revision) => {
                // Get the current URL
                const currentUrl = this.router.url;

                // Find the position of the first "/"
                const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1))

                // Navigate to the modified URL
                this.router.navigate([baseUrl + "/" + revision.revision.id]).then(() => {
                    // Reload the page after navigation
                    window.location.reload();
                });
                
                this.appnotificationService.notification$.next({ message:translate('revisions.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Revision not created successfully
    createRevisionFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(RevisionActions.createRevisionFailure),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('revisions.createFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );




    //////////////////////
    // UPDATE REVISION //
    ////////////////////

    // Update Revision
    updateRevision$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.updateRevision),
            mergeMap((action) => 
                this.revisionService.updateRevision(action.revision).pipe(
                    map((revision: IUpdateRevisionResponse) => RevisionActions.updateRevisionSuccess({ revision })),
                    catchError((error) => of(RevisionActions.updateRevisionFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Revision successful
    updateRevisionSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.updateRevisionSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('revisions.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Revision not updated successfully
    updateRevisionFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.updateRevisionFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('revisions.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );



    //////////////////////
    // DELETE REVISION //
    ////////////////////

    // Delete Revision
    deleteRevision$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.deleteRevision), 
            mergeMap((action) => {
                return this.revisionService.deleteRevision(action.id).pipe(
                    map(() => RevisionActions.deleteRevisionSuccess({id: action.id})), 
                    catchError((error) => of(RevisionActions.deleteRevisionFailure({ error: error.message })))
            )}
        ))
    );

    // Revision deleted successfully
    deleteRevisionSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(RevisionActions.deleteRevisionSuccess),
          tap(() => {
            // Get the current URL
            const currentUrl = this.router.url;

            // Find the position of the first "/"
            const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1));

            // Navigate to the modified URL
            this.router.navigate([baseUrl]);
              this.appnotificationService.notification$.next({ message:translate('revisions.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Revision not deleted successfully
    deleteRevisionFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RevisionActions.deleteRevisionFailure)
      ), { dispatch: false }
    );

    // Delete device from revision
    deleteDeviceFromRevision$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.deleteDeviceFromRevision), 
            mergeMap((action) => {
                return this.revisionService.deleteDeviceFromRevision(action.request).pipe(
                    map((revision: IUpdateRevisionResponse) => RevisionActions.deleteDeviceFromRevisionSuccess({ revision })), 
                    catchError((error) => of(RevisionActions.deleteDeviceFromRevisionFailure({ error: error.message })))
            )}
        ))
    );

    // Successfully deleted device from revision
    deleteDeviceFromRevisionSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.deleteDeviceFromRevisionSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('revisions.deleteDeviceFromRevisionSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Not successfully deleted device from revision
    deleteDeviceFromRevisionFailure$ = createEffect(() =>
        this.actions$.pipe(
        ofType(RevisionActions.deleteDeviceFromRevisionFailure),
        tap(() => {
            this.appnotificationService.notification$.next({ message:translate('revisions.deleteDeviceFromRevisionFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );

    ////////////////////////
    // COMPLETE REVISION //
    //////////////////////

    completeRevision$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevisionActions.completeRevision),
            mergeMap((action) => 
                this.revisionService.completeRevision(action).pipe(
                    map((revision: IUpdateRevisionResponse) => RevisionActions.completeRevisionSuccess( {revision} )),
                    catchError((error) => of(RevisionActions.completeRevisionFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Complete Revision successful
    completeRevisionSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.completeRevisionSuccess),
            tap(() => {
                // Get the current URL
                const currentUrl = this.router.url;

                // Find the position of the first "/"
                const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1))

                this.router.navigate([baseUrl]);

                this.appnotificationService.notification$.next({ message:translate('revisions.completeSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Revision not completed successfully
    completeRevisionFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(RevisionActions.completeRevisionFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('revisions.completeFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    constructor(
        private actions$: Actions, 
        private router: Router,
        private revisionService: RevisionService,
        private appnotificationService: AppNotificationService) {}
}